import { makeStyles } from 'tss-react/mui';

type SidebarProps = {
  open?: boolean;
};

export const sidebarStyles = makeStyles<SidebarProps>()((theme, { open }) => ({
  // ─── Root: fixed sidebar (desktop only) ───────────────────────────────────────
  root: {
    display: 'none',
    [theme.breakpoints.up('md')]: {
      display: 'flex',
      flexDirection: 'column',
      // shadcn default: --sidebar-width = 16rem (256px)
      width: 256,
      height: '100vh',
      position: 'fixed',
      top: 0,
      left: 0,
      zIndex: 1200,
      background: '#FFFFFF',
      borderRight: '1px solid #E5E7EB',
      overflow: 'hidden',
      transition: 'width 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    },
  },
  rootCollapsed: {
    [theme.breakpoints.up('md')]: {
      width: 56,
    },
  },
  // Icon-only centering (used when sidebar is collapsed)
  menuButtonIconOnly: {
    justifyContent: 'center',
    padding: '8px 0',
    gap: 0,
  },
  teamButtonIconOnly: {
    justifyContent: 'center',
    padding: '8px 0',
    gap: 0,
  },
  userButtonIconOnly: {
    justifyContent: 'center',
    padding: '8px 0',
    gap: 0,
  },

  // ─── SidebarHeader ────────────────────────────────────────────────────────────
  sidebarHeader: {
    padding: '8px',
    // No border-bottom — shadcn doesn't have one on SidebarHeader
  },

  // ─── TeamSwitcher (SidebarMenuButton size="lg") ───────────────────────────────
  teamButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    // size="lg" → padding: 8px, height ~auto
    padding: '8px',
    borderRadius: 8,
    cursor: 'pointer',
    border: 'none',
    background: 'transparent',
    textAlign: 'left' as const,
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: 'hsl(240 4.8% 95.9%)', // sidebar-accent
    },
  },
  teamLogo: {
    width: 36,
    height: 36,
    borderRadius: 10,
    overflow: 'hidden',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& img': {
      width: 36,
      height: 36,
      objectFit: 'contain',
    },
  },
  teamLogoFallback: {
    width: 36,
    height: 36,
    borderRadius: 10,
    background: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontSize: 14,
    fontWeight: 700,
    fontFamily: 'Inter, sans-serif',
    flexShrink: 0,
  },
  teamInfo: {
    // grid flex-1 text-left text-sm leading-tight
    display: 'grid',
    flex: 1,
    textAlign: 'left' as const,
    fontSize: '14px',
    lineHeight: 1.25,
    minWidth: 0,
  },
  teamName: {
    // truncate font-medium
    fontWeight: 500,
    fontFamily: 'Inter, sans-serif',
    color: 'hsl(240 10% 3.9%)', // sidebar-foreground
    margin: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    fontSize: '14px',
    lineHeight: 1.25,
  },
  teamPlan: {
    // truncate text-xs
    fontWeight: 400,
    fontFamily: 'Inter, sans-serif',
    color: 'hsl(240 3.8% 46.1%)', // muted-foreground
    margin: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    fontSize: '12px',
    lineHeight: 1.25,
  },
  chevronsIcon: {
    marginLeft: 'auto',
    color: 'hsl(240 3.8% 46.1%)',
    flexShrink: 0,
  },

  // ─── SidebarContent (scrollable) ──────────────────────────────────────────────
  sidebarContent: {
    flex: 1,
    overflowY: 'auto' as const,
    overflowX: 'hidden' as const,
    // shadcn SidebarContent: flex min-h-0 flex-1 flex-col gap-2 overflow-auto
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 4,
    padding: '0 8px',
    // Thin scrollbar
    '&::-webkit-scrollbar': {
      width: 4,
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: 2,
      backgroundColor: 'transparent',
    },
    '&:hover::-webkit-scrollbar-thumb': {
      backgroundColor: 'hsl(220 13% 85%)',
    },
  },

  // ─── SidebarGroup ─────────────────────────────────────────────────────────────
  sidebarGroup: {
    // relative flex w-full min-w-0 flex-col p-2
    position: 'relative' as const,
    display: 'flex',
    flexDirection: 'column' as const,
    width: '100%',
    minWidth: 0,
    padding: 8,
  },
  sidebarGroupCollapsed: {
    alignItems: 'center',
  },
  // Thin separator between groups when collapsed
  collapsedSeparator: {
    width: 24,
    height: 1,
    backgroundColor: 'hsl(220 13% 91%)',
    margin: '4px auto',
    flexShrink: 0,
  },
  // SidebarGroupLabel
  groupLabel: {
    // flex h-8 shrink-0 items-center rounded-md px-2 text-xs font-medium text-sidebar-foreground/70
    display: 'flex',
    height: 32,
    flexShrink: 0,
    alignItems: 'center',
    borderRadius: 6,
    padding: '0 8px',
    fontSize: '11px',
    fontWeight: 500,
    fontFamily: 'Inter, sans-serif',
    color: 'hsl(240 5.3% 26.1% / 0.7)', // sidebar-foreground/70
    userSelect: 'none' as const,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.06em',
  },

  // ─── SidebarMenu ─────────────────────────────────────────────────────────────
  sidebarMenu: {
    // flex w-full min-w-0 flex-col gap-1
    display: 'flex',
    flexDirection: 'column' as const,
    width: '100%',
    minWidth: 0,
    gap: 2,
    listStyle: 'none',
    margin: 0,
    padding: 0,
  },

  // ─── SidebarMenuButton (default size) ─────────────────────────────────────────
  menuButton: {
    position: 'relative' as const,
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    gap: 10,
    borderRadius: 8,
    padding: '10px',
    textAlign: 'left' as const,
    fontSize: '14px',
    fontFamily: 'Inter, sans-serif',
    fontWeight: 500,
    lineHeight: 1.4,
    color: '#1F1333',
    cursor: 'pointer',
    textDecoration: 'none',
    border: 'none',
    background: 'transparent',
    transition: 'background-color 0.15s ease, color 0.15s ease',
    '&:hover': {
      backgroundColor: '#F4F1F9',
      color: '#1F1333',
    },
    '&:active': {
      backgroundColor: '#EDE8F5',
    },
  },
  menuButtonActive: {
    backgroundColor: `${theme.palette.primary.main}14`,
    color: theme.palette.primary.main,
    fontWeight: 600,
    '&:hover': {
      backgroundColor: `${theme.palette.primary.main}1E`,
      color: theme.palette.primary.main,
    },
  },
  // Active state when sidebar is collapsed (icon-only)
  menuButtonIconOnlyActive: {
    backgroundColor: `${theme.palette.primary.main}18`,
    borderLeft: 'none',
    padding: '6px 0',
    borderRadius: '8px',
    '& svg': {
      width: '20px',
      height: '20px',
    },
  },

  // ─── Collapsible trigger (same as menuButton but with chevron) ────────────────
  chevron: {
    // ml-auto transition-transform duration-200 group-data-[state=open]:rotate-90
    marginLeft: 'auto',
    transition: 'transform 0.2s ease',
    color: 'hsl(240 3.8% 46.1%)',
    flexShrink: 0,
  },
  chevronOpen: {
    transform: 'rotate(90deg)',
  },

  // ─── SidebarMenuSub (sub-items) ──────────────────────────────────────────────
  subMenu: {
    // mx-3.5 flex min-w-0 translate-x-px flex-col gap-1
    // border-l border-sidebar-border px-2.5 py-0.5
    margin: '0 14px',
    display: 'flex',
    flexDirection: 'column' as const,
    minWidth: 0,
    gap: 2,
    borderLeft: '1px solid hsl(220 13% 91%)', // sidebar-border
    padding: '2px 10px',
    listStyle: 'none',
  },
  // SidebarMenuSubButton
  subMenuButton: {
    // flex h-7 min-w-0 items-center gap-2 rounded-md px-2 text-sidebar-foreground
    // hover:bg-sidebar-accent hover:text-sidebar-accent-foreground
    display: 'flex',
    height: 28,
    minWidth: 0,
    alignItems: 'center',
    gap: 8,
    borderRadius: 6,
    padding: '0 8px',
    fontSize: '13px',
    fontFamily: 'Inter, sans-serif',
    fontWeight: 400,
    color: 'hsl(240 5.3% 26.1%)',
    cursor: 'pointer',
    textDecoration: 'none',
    transition: 'background-color 0.15s ease, color 0.15s ease',
    '&:hover': {
      backgroundColor: 'hsl(240 4.8% 95.9%)',
      color: 'hsl(240 5.9% 10%)',
    },
  },
  subMenuButtonActive: {
    color: theme.palette.primary.main,
    fontWeight: 600,
    paddingLeft: 5, // compensate for the 3px border (was 8px → 8-3=5)
    '&:hover': {
      backgroundColor: `${theme.palette.primary.main}18`,
      color: theme.palette.primary.main,
    },
  },

  // ─── Badge ────────────────────────────────────────────────────────────────────
  badge: {
    marginLeft: 'auto',
    background: theme.palette.warning.main,
    color: '#fff',
    fontSize: !open ? 9 : 11,
    fontWeight: 600,
    borderRadius: 6,
    padding: open ? '2px 7px' : '3px 6px',
    fontFamily: 'Inter, sans-serif',
    lineHeight: open ? 1.4 : 1,
    position: open ? 'relative' : 'absolute',
    right: open ? 'auto' : -8,
    top: open ? 'auto' : 0,
  },

  // ─── SidebarFooter ───────────────────────────────────────────────────────────
  sidebarFooter: {
    // flex flex-col gap-2 p-2
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 8,
    padding: 8,
    borderTop: '1px solid hsl(220 13% 91%)',
    flexShrink: 0,
  },
  // NavUser button (SidebarMenuButton size="lg")
  userButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    padding: '10px',
    borderRadius: 10,
    cursor: 'pointer',
    border: '1px solid #E5E7EB',
    background: '#F9FAFB',
    textDecoration: 'none',
    textAlign: 'left' as const,
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: '#F1F3F5',
    },
  },
  // Avatar: circular + solid primary
  userAvatar: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    background: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontSize: 13,
    fontWeight: 700,
    fontFamily: 'Inter, sans-serif',
    flexShrink: 0,
  },
  userInfo: {
    display: 'grid',
    flex: 1,
    textAlign: 'left' as const,
    fontSize: '14px',
    lineHeight: 1.25,
    minWidth: 0,
  },
  userName: {
    fontWeight: 500,
    fontFamily: 'Inter, sans-serif',
    color: 'hsl(240 10% 3.9%)',
    margin: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    fontSize: '14px',
    lineHeight: 1.25,
  },
  userEmail: {
    fontWeight: 400,
    fontFamily: 'Inter, sans-serif',
    color: 'hsl(240 3.8% 46.1%)',
    margin: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    fontSize: '12px',
    lineHeight: 1.25,
  },

  // ─── Desktop top bar (SidebarInset header) ────────────────────────────────────
  topBar: {
    display: 'none',
    [theme.breakpoints.up('md')]: {
      // flex h-16 shrink-0 items-center gap-2
      display: 'flex',
      height: 64, // h-16
      flexShrink: 0,
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 8,
      transition: 'width 0.15s linear, height 0.15s linear',
      borderBottom: '1px solid hsl(220 13% 91%)',
      position: 'sticky',
      top: 0,
      zIndex: 100,
      backgroundColor: 'white',
    },
  },
  topBarInner: {
    // flex items-center gap-2 px-4
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '0 16px',
  },
  // SidebarTrigger
  sidebarTrigger: {
    // -ml-1 → marginLeft: -4px
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    borderRadius: 6,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    color: 'hsl(240 3.8% 46.1%)',
    marginLeft: -4,
    transition: 'background-color 0.15s ease, color 0.15s ease',
    flexShrink: 0,
    '&:hover': {
      backgroundColor: 'hsl(240 4.8% 95.9%)',
      color: 'hsl(240 5.9% 10%)',
    },
  },
  // Separator vertical: mr-2 h-4
  topBarSeparator: {
    width: 1,
    height: 16, // h-4
    backgroundColor: 'hsl(220 13% 91%)',
    marginRight: 8, // mr-2
    flexShrink: 0,
  },
  // Breadcrumb
  breadcrumbList: {
    // flex flex-wrap items-center gap-1.5 break-words text-sm text-muted-foreground sm:gap-2.5
    display: 'flex',
    flexWrap: 'wrap' as const,
    alignItems: 'center',
    gap: 6,
    listStyle: 'none',
    margin: 0,
    padding: 0,
    fontSize: '14px',
    color: 'hsl(240 3.8% 46.1%)', // muted-foreground
    fontFamily: 'Inter, sans-serif',
  },
  breadcrumbLink: {
    // transition-colors hover:text-foreground
    color: 'hsl(240 3.8% 46.1%)',
    textDecoration: 'none',
    transition: 'color 0.15s ease',
    fontWeight: 400,
    '&:hover': {
      color: 'hsl(240 10% 3.9%)', // foreground
    },
  },
  breadcrumbPage: {
    // font-normal text-foreground
    fontWeight: 400,
    color: 'hsl(240 10% 3.9%)',
  },
  breadcrumbSeparator: {
    // [&>svg]:h-3.5 [&>svg]:w-3.5
    display: 'flex',
    alignItems: 'center',
    color: 'hsl(240 3.8% 46.1%)',
  },

  // ─── User dropdown (Popover) ──────────────────────────────────────────────
  userDropdown: {
    // w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg
    minWidth: 224, // min-w-56
    borderRadius: 12,
    border: '1px solid hsl(220 13% 91%)',
    boxShadow:
      '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1)',
    padding: '4px',
    fontFamily: 'Inter, sans-serif',
  },
  dropdownHeader: {
    // flex items-center gap-2 px-1 py-1.5 text-left text-sm
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 8px',
    fontSize: '14px',
  },
  dropdownSeparator: {
    height: 1,
    backgroundColor: 'hsl(220 13% 91%)',
    margin: '4px -4px',
  },
  dropdownGroup: {
    padding: '4px 0',
  },
  dropdownItem: {
    // relative flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    padding: '6px 8px',
    borderRadius: 6,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: '14px',
    fontFamily: 'Inter, sans-serif',
    fontWeight: 400,
    color: 'hsl(240 10% 3.9%)',
    textAlign: 'left' as const,
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: 'hsl(240 4.8% 95.9%)',
    },
    '& svg': {
      color: 'hsl(240 3.8% 46.1%)', // text-muted-foreground for icons
    },
  },
  // Small avatar in TopBar (user initials)
  topBarAvatar: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    background: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontSize: 13,
    fontWeight: 700,
    fontFamily: 'Inter, sans-serif',
    flexShrink: 0,
    cursor: 'pointer',
  },
  // "company · #5" sub text in footer
  userSubText: {
    fontWeight: 400,
    fontFamily: 'Inter, sans-serif',
    color: 'hsl(240 3.8% 46.1%)',
    margin: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    fontSize: '12px',
    lineHeight: 1.25,
  },
  // Company dot indicator
  companyDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: theme.palette.success.main,
    flexShrink: 0,
  },
}));
